import { useId, useState } from 'react'
import { Button, Dialog, Pagination } from '@bloo-team/ui'
import { CM_CATEGORIES, type CmSeverity } from '@bloo-team/api-client'
import { SEVERITY_ORDER, reasonSentence, type Signal, type SignalSeverity } from '../../lib/signal-model'
import { agoLabel, caseLabel, CATEGORY_LABEL, sevKey, statusLabel } from '../../lib/case-mgmt'
import { NewCaseForm } from '../cases/CaseDialogs'
import type { EscalateDialogProps } from './types'

/**
 * Add to case (docs/cases.md §Escalate): New case (the New case
 * fields, title / description / severity prefilled from the signals) or
 * Existing case (a paged list of cases, status / category filters, one
 * radio). When the hook reports a `pending` create-whose-attach-failed,
 * the dialog only retries the attach — no orphan cases.
 */

/** Highest severity across the signals; 'medium' when there are none. */
export function worstSeverity(signals: Signal[]): SignalSeverity {
  let worst: SignalSeverity | null = null
  for (const s of signals) {
    if (worst === null || SEVERITY_ORDER.indexOf(s.severity) < SEVERITY_ORDER.indexOf(worst)) worst = s.severity
  }
  return worst ?? 'medium'
}

/** A signal (or storyline) severity as the case service spells it. */
export const toCaseSeverity = (s: string): CmSeverity => (s === 'info' ? 'informational' : (['critical', 'high', 'medium', 'low'].includes(s) ? s : 'medium') as CmSeverity)

export function EscalateDialog({ open, ...p }: EscalateDialogProps) {
  return (
    <Dialog open={open} onClose={p.onCancel} title="Add to case" className="sp-close-dialog" isDismissable={!p.busy}>
      {open ? <EscalateForm {...p} /> : null}
    </Dialog>
  )
}

function EscalateForm({ signals, cases, users, pending, busy, error = null, prefill, onCancel, onConfirm }: Omit<EscalateDialogProps, 'open'>) {
  const uid = useId()
  const first = signals[0]
  const [mode, setMode] = useState<'new' | 'existing'>(prefill?.mode ?? 'new')
  const [caseId, setCaseId] = useState('')
  const count = prefill?.count ?? signals.length

  const what = (
    <p className="sp-form-what">
      Attaching <b>{count}</b> signal{count === 1 ? '' : 's'}
    </p>
  )

  // Nothing to attach (a stale pick): a case with no signals is never created.
  if (count === 0 && !pending) {
    return (
      <div className="hud-stack sp-form">
        {what}
        <p className="sp-form-error" role="alert">
          Nothing to attach — a case with no signals is never created.
        </p>
        <div className="hud-row sp-form-actions">
          <Button variant="ghost" size="sm" tone="neutral" onPress={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    )
  }

  if (pending) {
    return (
      <div className="hud-stack sp-form">
        {what}
        <p className="sp-form-error" role="alert">
          {pending.caseNumber ?? 'The case'} was created but attaching {count} signal{count === 1 ? '' : 's'} failed: {pending.error}
        </p>
        <div className="hud-row sp-form-actions">
          <Button variant="ghost" size="sm" tone="neutral" isDisabled={busy} onPress={onCancel}>
            Cancel
          </Button>
          <Button variant="solid" size="sm" isDisabled={busy} onPress={() => onConfirm({ caseId: pending.caseId, caseNumber: pending.caseNumber })}>
            {busy ? 'Working…' : 'Retry attach'}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="hud-stack sp-form">
      {what}
      <div className="bui-toggles sp-target-toggle" role="group" aria-label="Case target">
        <button type="button" className="bui-toggle" aria-pressed={mode === 'new'} onClick={() => setMode('new')}>
          New case
        </button>
        <button type="button" className="bui-toggle" aria-pressed={mode === 'existing'} onClick={() => setMode('existing')}>
          Existing case
        </button>
      </div>

      {mode === 'new' ? (
        <NewCaseForm
          initialTitle={prefill?.title ?? (first ? `${first.detectionName}${first.entities[0] ? ` — ${first.entities[0].value}` : ''}` : '')}
          initialDescription={prefill?.description ?? signals.map((s) => reasonSentence(s)).join('\n')}
          initialSeverity={toCaseSeverity(prefill?.severity ?? worstSeverity(signals))}
          users={users}
          busy={busy}
          error={error}
          onCancel={onCancel}
          onConfirm={(i) => onConfirm({ title: i.title, description: i.description, severity: i.severity, assignee: i.assignee || undefined })}
        />
      ) : (
        <>
          <div className="hud-row sg-filters">
            <label className="hud-row cs-radio" htmlFor={`${uid}-status`}>
              <span className="hud-eyebrow">status</span>
              <select
                id={`${uid}-status`}
                className="bui-input"
                value={cases.status ?? ''}
                onChange={(e) => {
                  setCaseId('')
                  cases.onStatus(e.target.value || null)
                }}
              >
                <option value="">all</option>
                {cases.statuses.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="hud-row cs-radio" htmlFor={`${uid}-cat`}>
              <span className="hud-eyebrow">category</span>
              <select
                id={`${uid}-cat`}
                className="bui-input"
                value={cases.category ?? ''}
                onChange={(e) => {
                  setCaseId('')
                  cases.onCategory((e.target.value || null) as typeof cases.category)
                }}
              >
                <option value="">all</option>
                {CM_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {CATEGORY_LABEL[c]}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {cases.loading ? (
            <p className="q-facet-note" role="status">
              loading cases…
            </p>
          ) : cases.error ? (
            <p className="sp-form-error" role="alert">
              {cases.error}
            </p>
          ) : cases.items.length === 0 ? (
            <p className="q-facet-note">No cases match.</p>
          ) : (
            <div className="cs-attach-list" role="radiogroup" aria-label="Existing case">
              {cases.items.map((c) => {
                const id = `${uid}-${c.id}`
                return (
                  <div key={c.id} className="cs-attach-item">
                    <input id={id} type="radio" name={`${uid}-case`} checked={caseId === c.id} onChange={() => setCaseId(c.id)} />
                    <label htmlFor={id} className="cs-attach-row">
                      <span className="sp-sev" data-severity={sevKey(c.severity)} aria-hidden="true" />
                      <span className="cs-attach-name">
                        <span className="mono muted">{caseLabel(c)}</span> {c.title}
                      </span>
                      <span className="cs-status" data-category={c.category ?? undefined}>
                        {statusLabel(cases.statuses, c.status)}
                      </span>
                      <span className="mono muted">{agoLabel(c.updatedAt)}</span>
                      <span className="muted cs-attach-ents">{c.assignee ?? 'Unassigned'}</span>
                    </label>
                  </div>
                )
              })}
            </div>
          )}
          {cases.total > cases.pageSize ? (
            <Pagination page={cases.page} pageSize={cases.pageSize} total={cases.total} onPageChange={cases.onPage} busy={cases.loading} unit="cases" />
          ) : null}
          <div className="hud-row sp-form-actions">
            <Button variant="ghost" size="sm" tone="neutral" isDisabled={busy} onPress={onCancel}>
              Cancel
            </Button>
            <Button variant="solid" size="sm" isDisabled={!caseId || busy} onPress={() => onConfirm({ caseId, caseNumber: cases.items.find((c) => c.id === caseId)?.caseNumber ?? null })}>
              {busy ? 'Working…' : 'Add to case'}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
